<script setup>
import { computed, watch, toRef, ref } from 'vue'
import { Button } from '@/shadcn/components/ui/button'
import {
  ChevronLeftIcon,
  ChevronRightIcon
} from '@lucide/vue'

// Props and emits
const props = defineProps({
  data: { type: Object, required: true },
  limit: { type: Number, default: 2 },
  showPageNumbers: { type: Boolean, default: false },
})

const emit = defineEmits(['pagination-change-page'])

// Constants
const DEFAULT_PAGE_SIZE = 20
const MIN_PAGE = 1

// Reactive data
const raw = toRef(props, 'data')

// Override the computed properties to be directly reactive to data
const computedCurrentPage = computed(() => {
  const { currentPage: extractedPage } = extractPaginationData(raw.value)
  return extractedPage
})

const computedPageCount = computed(() => {
  const { pageSize, total } = extractPaginationData(raw.value)
  return total > 0 ? Math.ceil(total / pageSize) : 1
})

const computedIsFirstPage = computed(() => computedCurrentPage.value <= 1)
const computedIsLastPage = computed(() => computedCurrentPage.value >= computedPageCount.value)

// Computed pagination data for template use
const paginationData = computed(() => extractPaginationData(raw.value))

const shouldShowPagination = computed(() => {
  const hasData = props.data && (props.data.total || props.data?.meta?.total || 0) > 0
  const hasMultiplePages = computedPageCount.value > 1
  return hasData && hasMultiplePages
})

// Helper functions
const isValidNumber = (value) => Number.isFinite(value) && value >= 0

const extractPaginationData = (data) => {
  const meta = data?.meta || data || {}

  // Current page
  let currentPage = meta.current_page || MIN_PAGE

  // Page size
  let pageSize = meta.per_page || DEFAULT_PAGE_SIZE

  // Total
  let total = meta.total || 0

  return {
    currentPage: Number(currentPage),
    pageSize: Number(pageSize),
    total: Number(total)
  }
}

// Pagination action functions
const goToPage = (pageNum) => {
  const validPage = isValidNumber(pageNum) && pageNum >= MIN_PAGE ? pageNum : MIN_PAGE
  const maxPage = computedPageCount.value || MIN_PAGE
  const targetPage = Math.min(validPage, maxPage)

  emit('pagination-change-page', targetPage)
}

const goToPrev = () => {
  if (!computedIsFirstPage.value) {
    const prevPage = Math.max(1, computedCurrentPage.value - 1)
    emit('pagination-change-page', prevPage)
  }
}

const goToNext = () => {
  if (!computedIsLastPage.value) {
    const nextPage = Math.min(computedPageCount.value, computedCurrentPage.value + 1)
    emit('pagination-change-page', nextPage)
  }
}

// Generate page numbers with ellipsis
const pages = computed(() => {
  const current = computedCurrentPage.value
  const last = computedPageCount.value
  const limit = Math.max(0, props.limit)

  // Calculate window boundaries
  const windowStart = Math.max(2, current - limit)
  const windowEnd = Math.min(last - 1, current + limit)

  // Build page array
  const pageArray = [1]

  // Add left ellipsis if needed
  if (windowStart > 2) {
    pageArray.push('ellipsis-left')
  }

  // Add pages in window
  for (let page = windowStart; page <= windowEnd; page++) {
    if (page >= 2 && page <= last - 1) {
      pageArray.push(page)
    }
  }

  // Add right ellipsis if needed
  if (windowEnd < last - 1) {
    pageArray.push('ellipsis-right')
  }

  // Add last page if not already included
  if (last > 1) {
    pageArray.push(last)
  }

  // Insert current page if not in array and not first/last
  if (!pageArray.includes(current) && current !== 1 && current !== last) {
    const insertIndex = pageArray.findIndex(n => typeof n === 'number' && n > current)
    if (insertIndex === -1) {
      pageArray.push(current)
    } else {
      pageArray.splice(insertIndex, 0, current)
    }
  }

  // Remove duplicates while preserving order
  const seen = new Set()
  return pageArray.filter(page => {
    const key = String(page)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
})

</script>

<template>
  <nav
    class="tw:flex tw:items-center tw:gap-2 tw:justify-between tw:md:justify-end"
    role="navigation"
    aria-label="Pagination"
  >
    <!-- Pagination results text -->
    <span class="tw:text-sm tw:text-gray-600 tw:mx-2">
      {{ $t('Results {start}-{end} of {total}', {
        start: ((computedCurrentPage - 1) * paginationData.pageSize) + 1,
        end: Math.min(computedCurrentPage * paginationData.pageSize, paginationData.total),
        total: paginationData.total
      }) }}
    </span>

    <div v-if="shouldShowPagination" class="tw:flex tw:items-center tw:gap-2 tw:select-none">
      <!-- Previous button -->
      <Button
        variant="outline"
        size="sm"
        :disabled="computedIsFirstPage"
        @click="goToPrev"
      >
        <ChevronLeftIcon class="tw:size-4" />
        {{ $t('Previous') }}
      </Button>

      <!-- Page numbers -->
      <ul v-if="showPageNumbers" class="tw:flex tw:items-center tw:gap-1">
        <li v-for="(page, index) in pages" :key="index">
          <Button
            v-if="typeof page === 'number'"
            variant="outline"
            size="sm"
            :class="page === computedCurrentPage ? 'tw:font-semibold tw:border-black/60' : 'tw:opacity-80 hover:tw:opacity-100'"
            :aria-current="page === computedCurrentPage ? 'page' : undefined"
            @click="goToPage(page)"
          >
            {{ page }}
          </Button>
          <span v-else class="tw:px-2 tw:text-sm tw:opacity-60 tw:select-none">…</span>
        </li>
      </ul>

      <!-- Next button -->
      <Button
        variant="outline"
        size="sm"
        :disabled="computedIsLastPage"
        @click="goToNext"
      >
        {{ $t('Next') }}
        <ChevronRightIcon class="tw:size-4" />
      </Button>
    </div>
  </nav>
</template>
